import {
  isBlockingImportStatus,
  pipelineGitDetailOf,
  usePipelineGitImportApply,
  usePipelineGitImportCheck,
} from "@via/api/pipeline-git.api.tsx";
import Modal from "@via/components/modal";
import { StepProgress } from "@via/components/step-progress";
import { useIntegrationTokens } from "@via/api/integration-tokens.api.tsx";
import {
  usePipelineMenuGroupsList,
  useProcessMenuGroupsList,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import { PipelineGitImportRequestBody, ProcessMenuGroupType } from "common";
import { JSX, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  "already-imported": "Already here, reused",
  "update-available": "New revision of a local one",
  "uuid-conflict": "Conflicts with a local item",
  "contract-refused": "Refused",
  unreadable: "Unreadable",
};

const runnableLabel = (runnable: boolean | null): string =>
  null === runnable
    ? "n/a"
    : runnable
      ? "Yes"
      : "No, until a site admin trusts the repository";

type MenuGroup = { id: number; name: string };

/**
 * Import a whole pipeline from a git repository another installation exported it to.
 *
 * Three steps. Source: the https repository, an optional credential, the pipeline's folder and
 * an optional revision (empty means the highest published). Review: the server's check plan, one
 * row per pipeline, module and process, with what importing does to each and whether it will run
 * here; then the two menu groups and the consent. Result: what was created, with a link.
 *
 * The check stores nothing. The apply is one transaction on the server, so the plan shown is
 * what is imported or nothing is. A blocking row (a conflict, a refused or unreadable process)
 * keeps Import disabled, because the server would refuse the whole import anyway. Code from a
 * repository no site admin has trusted is imported but does not run; the consent says so, and
 * nothing on this screen can trust a repository.
 */
export default function ImportPipelineFromGitModal({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const [repoUrl, setRepoUrl] = useState("");
  const [slug, setSlug] = useState("");
  const [rev, setRev] = useState("");
  const [serviceTokenId, setServiceTokenId] = useState("");
  const [menuGroupId, setMenuGroupId] = useState("");
  const [pipelineGroupId, setPipelineGroupId] = useState("");
  const [consent, setConsent] = useState(false);
  const [checkedBody, setCheckedBody] =
    useState<PipelineGitImportRequestBody | null>(null);

  const check = usePipelineGitImportCheck();
  const apply = usePipelineGitImportApply();
  const plan = check.data;

  const { data: credentialPage } = useIntegrationTokens(0, 100);
  const credentials = useMemo(
    () =>
      [...(credentialPage?.data ?? [])].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [credentialPage]
  );
  const { data: processGroups } = useProcessMenuGroupsList(
    String(ProcessMenuGroupType.Process)
  );
  const { data: pipelineGroups } = usePipelineMenuGroupsList();

  const step = undefined !== apply.data ? 2 : undefined !== plan ? 1 : 0;
  const steps = [
    { name: "Source", isCompleted: step > 0 },
    { name: "Review", isCompleted: step > 1 },
    { name: "Result", isCompleted: undefined !== apply.data },
  ];

  const runCheck = (): void => {
    const body: PipelineGitImportRequestBody = {
      repoUrl: repoUrl.trim(),
      slug: slug.trim(),
      rev: "" === rev.trim() ? null : Number(rev),
      serviceTokenId: "" === serviceTokenId ? null : Number(serviceTokenId),
    };
    setCheckedBody(body);
    setConsent(false);
    check.mutate(body);
  };

  const revIsValid = "" === rev.trim() || /^\d+$/.test(rev.trim());
  const canCheck =
    "" !== repoUrl.trim() &&
    "" !== slug.trim() &&
    revIsValid &&
    !check.isPending;
  const canImport =
    undefined !== plan &&
    plan.canApply &&
    null !== checkedBody &&
    "" !== menuGroupId &&
    "" !== pipelineGroupId &&
    consent &&
    !apply.isPending;

  const sourceStep = (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Point at the repository a pipeline was exported to. Nothing is stored
        until you review the plan and import it.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <input
          className="rounded border border-gray-300 px-2 py-1 text-sm sm:col-span-4"
          placeholder="https://github.com/owner/repo.git"
          value={repoUrl}
          aria-label="Repository URL"
          onChange={event => setRepoUrl(event.target.value)}
        />
        <input
          className="rounded border border-gray-300 px-2 py-1 text-sm sm:col-span-2"
          placeholder="pipeline-folder"
          value={slug}
          aria-label="Pipeline folder"
          onChange={event => setSlug(event.target.value)}
        />
        <input
          className="rounded border border-gray-300 px-2 py-1 text-sm"
          placeholder="latest"
          value={rev}
          aria-label="Revision"
          onChange={event => setRev(event.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-2 py-1 text-sm"
          value={serviceTokenId}
          aria-label="Credential"
          onChange={event => setServiceTokenId(event.target.value)}>
          <option value="">No credential (public repository)</option>
          {credentials.map(credential => (
            <option key={credential.id} value={String(credential.id)}>
              {credential.name} ({credential.tokenType})
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-500">
        The folder is the pipeline&apos;s name under <code>pipelines/</code> in
        the repository. Leave the revision empty for the highest published one.
      </p>
      {!revIsValid ? (
        <p className="text-sm text-red-600">
          The revision is a whole number, or empty.
        </p>
      ) : null}
      {check.isError ? (
        <p
          className="text-sm text-red-600"
          data-testid="importPipelineCheckError">
          {pipelineGitDetailOf(
            check.error,
            "The repository could not be read. Check the URL, the folder and the credential."
          )}
        </p>
      ) : null}
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="importPipelineCheck"
          disabled={!canCheck}
          onClick={runCheck}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400">
          {check.isPending ? "Checking..." : "Check repository"}
        </button>
      </div>
    </div>
  );

  const reviewStep =
    undefined === plan ? null : (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Revision {plan.rev} of <span className="font-mono">{plan.slug}</span>{" "}
          ({plan.tag}).
        </p>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-1 pr-3">Kind</th>
              <th className="py-1 pr-3">Name</th>
              <th className="py-1 pr-3">Import</th>
              <th className="py-1">Runs here</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {plan.items.map(item => (
              <tr
                key={`${item.kind}:${item.key}`}
                data-testid="importPipelinePlanRow"
                className={
                  isBlockingImportStatus(item.status) ? "bg-red-50" : ""
                }>
                <td className="py-1 pr-3">{item.kind}</td>
                <td className="py-1 pr-3">{item.name}</td>
                <td className="py-1 pr-3">
                  {STATUS_LABELS[item.status] ?? item.status}
                  {null !== item.statusReason ? (
                    <span className="block text-xs text-gray-500">
                      {item.statusReason}
                    </span>
                  ) : null}
                </td>
                <td className="py-1">{runnableLabel(item.runnable)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {!plan.canApply ? (
          <p
            className="text-sm text-red-600"
            data-testid="importPipelineBlocked">
            This pipeline cannot be imported until the highlighted items are
            resolved. Nothing has been stored.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-sm text-gray-900">
                Process menu group
                <select
                  className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  value={menuGroupId}
                  aria-label="Process menu group"
                  onChange={event => setMenuGroupId(event.target.value)}>
                  <option value="">Select a menu group</option>
                  {(processGroups?.data ?? []).map((group: MenuGroup) => (
                    <option key={group.id} value={String(group.id)}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-gray-900">
                Pipeline menu group
                <select
                  className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  value={pipelineGroupId}
                  aria-label="Pipeline menu group"
                  onChange={event => setPipelineGroupId(event.target.value)}>
                  <option value="">Select a menu group</option>
                  {(pipelineGroups?.data ?? []).map((group: MenuGroup) => (
                    <option key={group.id} value={String(group.id)}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900">
              <input
                type="checkbox"
                className="mt-1"
                checked={consent}
                aria-label="Consent"
                onChange={event => setConsent(event.target.checked)}
              />
              <span>
                I am importing code from{" "}
                <span className="font-mono break-all">
                  {checkedBody?.repoUrl}
                </span>
                . Processes from a repository a site admin has not trusted are
                stored but do not run until one does.
              </span>
            </label>
          </>
        )}

        {apply.isError ? (
          <p
            className="text-sm text-red-600"
            data-testid="importPipelineApplyError">
            {pipelineGitDetailOf(
              apply.error,
              "The import failed. Nothing was stored."
            )}
          </p>
        ) : null}
        <div className="flex justify-between">
          <button
            type="button"
            disabled={apply.isPending}
            onClick={() => {
              check.reset();
              apply.reset();
            }}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:text-gray-400">
            Back
          </button>
          <button
            type="button"
            data-testid="importPipelineApply"
            disabled={!canImport}
            onClick={() =>
              null !== checkedBody &&
              apply.mutate({
                ...checkedBody,
                // Pin what was reviewed: a newer tag pushed since the check must not slip in.
                rev: plan.rev,
                menuGroupId: Number(menuGroupId),
                pipelineGroupId: Number(pipelineGroupId),
              })
            }
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400">
            {apply.isPending ? "Importing..." : "Import"}
          </button>
        </div>
      </div>
    );

  const result = apply.data;
  const resultStep =
    undefined === result ? null : (
      <div className="space-y-3 text-sm" data-testid="importPipelineResult">
        {null === result.pipelineId ? (
          <p className="text-gray-700">
            This pipeline revision is already on this installation, but it is
            not shared with you. Ask its owner for access.
          </p>
        ) : (
          <p className="text-gray-900">
            {result.created
              ? "Imported. "
              : "Already on this installation, nothing new was stored. "}
            <Link
              to={`/pipelines/${result.pipelineId}`}
              onClick={() => setOpen(false)}
              className="text-indigo-600 hover:underline">
              Open the pipeline
            </Link>
          </p>
        )}
        {0 < result.processIds.length ? (
          <p className="text-gray-500">
            {result.processIds.length} process
            {1 === result.processIds.length ? "" : "es"} imported with it.
          </p>
        ) : null}
      </div>
    );

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Import Pipeline From Git"
      size={ModalSize.Large}
      hasCancelButton={false}
      disableClose={apply.isPending}
      onBeforeClose={() => !apply.isPending}>
      <div className="space-y-4">
        <StepProgress steps={steps} currentStepIndex={step} />
        <div className="mt-6">
          {0 === step ? sourceStep : 1 === step ? reviewStep : resultStep}
        </div>
      </div>
    </Modal>
  );
}
